import React from 'react';
import { ArrowLeft, FileText, Book, Zap, ExternalLink, Lightbulb, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * Get badge variant based on document category
 */
function getCategoryBadge(category) {
  switch (category) {
    case 'quickstart':
      return { label: 'Quick Start', variant: 'default', className: 'bg-amber-500 hover:bg-amber-600' };
    case 'rules':
      return { label: 'Rules', variant: 'default', className: 'bg-purple-500 hover:bg-purple-600' };
    case 'notifications':
      return { label: 'Notifications', variant: 'default', className: 'bg-blue-500 hover:bg-blue-600' };
    case 'activity':
      return { label: 'Activity', variant: 'default', className: 'bg-green-500 hover:bg-green-600' };
    case 'general':
      return { label: 'Guide', variant: 'secondary', className: '' };
    default:
      return { label: category, variant: 'outline', className: '' };
  }
}

/**
 * Get icon component based on document category
 */
function getCategoryIcon(category) {
  switch (category) {
    case 'quickstart':
      return Zap;
    case 'rules':
    case 'notifications':
    case 'activity':
      return FileText;
    default:
      return Book;
  }
}

/**
 * HelpArticle - Renders a documentation article
 *
 * @param {Object} doc - The document object from the index
 * @param {Function} onBack - Callback to return to search view
 */
export function HelpArticle({ doc, onBack }) {
  if (!doc) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-slate-500">Article not found.</p>
          <Button variant="link" onClick={onBack} className="mt-2 p-0">
            Return to search
          </Button>
        </CardContent>
      </Card>
    );
  }

  const badge = getCategoryBadge(doc.category);
  const Icon = getCategoryIcon(doc.category);

  return (
    <div className="space-y-4">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="text-slate-600 hover:text-slate-900 -ml-2"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Help Center
      </Button>

      {/* Article card */}
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                doc.category === 'quickstart' ? 'bg-amber-100' :
                doc.category === 'rules' ? 'bg-purple-100' :
                doc.category === 'notifications' ? 'bg-blue-100' :
                doc.category === 'activity' ? 'bg-green-100' :
                'bg-slate-100'
              }`}>
                <Icon className={`h-5 w-5 ${
                  doc.category === 'quickstart' ? 'text-amber-600' :
                  doc.category === 'rules' ? 'text-purple-600' :
                  doc.category === 'notifications' ? 'text-blue-600' :
                  doc.category === 'activity' ? 'text-green-600' :
                  'text-slate-600'
                }`} />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">{doc.title}</CardTitle>
                <p className="text-slate-600 mt-1">{doc.description}</p>
              </div>
            </div>
            <Badge className={badge.className}>{badge.label}</Badge>
          </div>
        </CardHeader>

        <CardContent className="prose prose-slate max-w-none">
          {/* Render content sections if available */}
          {doc.content && doc.content.length > 0 ? (
            <div className="space-y-8">
              {doc.content.map((section, index) => (
                <section key={index} className="not-prose">
                  {/* Section heading - supports both 'title' and 'heading' */}
                  <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
                    {section.title || section.heading}
                  </h2>

                  {/* Render markdown-like content */}
                  {section.content && (
                    <div className="prose prose-slate prose-sm max-w-none">
                      {section.content.split('\n\n').map((paragraph, pIndex) => {
                        // Handle tables
                        if (paragraph.includes('|') && paragraph.includes('---')) {
                          const lines = paragraph.trim().split('\n');
                          const headers = lines[0].split('|').filter(h => h.trim());
                          const rows = lines.slice(2).map(row =>
                            row.split('|').filter(cell => cell.trim())
                          );
                          return (
                            <div key={pIndex} className="my-4 overflow-x-auto">
                              <table className="min-w-full border border-slate-200 rounded-lg overflow-hidden">
                                <thead className="bg-slate-50">
                                  <tr>
                                    {headers.map((header, hIndex) => (
                                      <th key={hIndex} className="px-4 py-2 text-left text-sm font-medium text-slate-700 border-b border-slate-200">
                                        {header.trim()}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((row, rIndex) => (
                                    <tr key={rIndex} className={rIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                      {row.map((cell, cIndex) => (
                                        <td key={cIndex} className="px-4 py-2 text-sm text-slate-600 border-b border-slate-200">
                                          {cell.trim().replace(/\*\*/g, '')}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        }

                        // Handle bullet lists
                        if (paragraph.trim().startsWith('- ') || paragraph.trim().startsWith('* ')) {
                          const items = paragraph.trim().split('\n').filter(line => line.trim());
                          return (
                            <ul key={pIndex} className="my-4 space-y-2">
                              {items.map((item, iIndex) => (
                                <li key={iIndex} className="flex items-start gap-2 text-slate-600">
                                  <span className="text-slate-400 mt-1.5">-</span>
                                  <span dangerouslySetInnerHTML={{
                                    __html: item.replace(/^[-*]\s*/, '')
                                      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
                                  }} />
                                </li>
                              ))}
                            </ul>
                          );
                        }

                        // Handle numbered lists
                        if (/^\d+\.\s/.test(paragraph.trim())) {
                          const items = paragraph.trim().split('\n').filter(line => line.trim());
                          return (
                            <ol key={pIndex} className="my-4 space-y-2 list-decimal list-inside">
                              {items.map((item, iIndex) => (
                                <li key={iIndex} className="text-slate-600">
                                  <span dangerouslySetInnerHTML={{
                                    __html: item.replace(/^\d+\.\s*/, '')
                                      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
                                  }} />
                                </li>
                              ))}
                            </ol>
                          );
                        }

                        // Regular paragraph with bold text support
                        return (
                          <p key={pIndex} className="my-4 text-slate-600 leading-relaxed"
                             dangerouslySetInnerHTML={{
                               __html: paragraph
                                 .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
                                 .replace(/\*([^*]+)\*/g, '<em>$1</em>')
                             }}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Render steps as numbered list with checkmarks */}
                  {section.steps && section.steps.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {section.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
                            {stepIndex + 1}
                          </div>
                          <p className="text-slate-700 text-sm leading-relaxed"
                             dangerouslySetInnerHTML={{
                               __html: step
                                 .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>')
                             }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Render subsections */}
                  {section.subsections && section.subsections.length > 0 && (
                    <div className="mt-6 space-y-4">
                      {section.subsections.map((sub, subIndex) => (
                        <div key={subIndex} className="border-l-2 border-slate-200 pl-4">
                          <h3 className="font-semibold text-slate-800 mb-2">{sub.title}</h3>
                          {sub.description && (
                            <p className="text-slate-600 text-sm mb-2 whitespace-pre-line"
                               dangerouslySetInnerHTML={{
                                 __html: sub.description
                                   .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
                               }}
                            />
                          )}
                          {sub.items && sub.items.length > 0 && (
                            <ul className="space-y-1.5 mt-2">
                              {sub.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="flex items-start gap-2 text-sm text-slate-600">
                                  <span className="text-slate-400 mt-0.5">-</span>
                                  <span dangerouslySetInnerHTML={{
                                    __html: item
                                      .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
                                  }} />
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Render example box */}
                  {section.example && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-800 text-sm">{section.example.title}</h4>
                          <p className="text-amber-700 text-sm mt-1 whitespace-pre-line"
                             dangerouslySetInnerHTML={{
                               __html: section.example.description
                                 .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>')
                             }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Render tips */}
                  {section.tips && section.tips.length > 0 && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-blue-800 text-sm mb-2">Tips</h4>
                          <ul className="space-y-1.5">
                            {section.tips.map((tip, tipIndex) => (
                              <li key={tipIndex} className="flex items-start gap-2 text-sm text-blue-700">
                                <span className="text-blue-400 mt-0.5">-</span>
                                <span>{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Render quick links section */}
                  {section.links && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      {section.links.map((link, lIndex) => (
                        <Link
                          key={lIndex}
                          to={link.path}
                          className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors group"
                        >
                          <div>
                            <div className="font-medium text-slate-900 group-hover:text-primary">
                              {link.label}
                            </div>
                            <div className="text-sm text-slate-500">
                              {link.description}
                            </div>
                          </div>
                          <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-primary" />
                        </Link>
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          ) : (
            /* Placeholder for docs without content yet */
            <div className="bg-slate-50 rounded-lg p-6 text-center">
              <div className="text-slate-400 mb-4">
                <Book className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                Content Coming Soon
              </h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                This documentation article is being prepared.
                In the meantime, you can explore other help topics or contact support.
              </p>
            </div>
          )}

          {/* Document metadata */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Related Topics</h4>
            <div className="flex flex-wrap gap-2">
              {doc.keywords.slice(0, 5).map((keyword, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default HelpArticle;
